import React from 'react';
import { useTheme } from '../../../../contexts/ThemeContext';
import { cn } from '../../../../lib/utils';
import { useFlexibleList } from '../../../../hooks/useSWR';

const ComplaintTimelineTab = ({ 
  selectedRecord
}) => {
  const { theme } = useTheme();
  const formatDateDDMMYYYY = (dateValue) => {
    if (!dateValue) return 'Unknown time';
    try {
      return new Date(dateValue).toLocaleDateString('en-GB');
    } catch {
      return 'Unknown time';
    }
  };

  // Fetch Complaint timeline entries for the selected record
  const { data: timelineEntries = [], isLoading, error } = useFlexibleList(
    selectedRecord?.id ? `/complaint-timeline/by-complaint/${selectedRecord.id}` : null
  );

  if (!selectedRecord) {
    return (
      <div className={cn("p-4 text-sm", theme.text.secondary)}>
        No Complaint selected
      </div>
    );
  }

  // Function to get action icon based on action type
  const getActionIcon = (action) => {
    const iconClass = "w-4 h-4";
    switch (action?.toUpperCase()) {
      case 'OPENED':
      case 'OPEN':
        return (
          <svg className={cn(iconClass, "text-green-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case 'CLOSED':
      case 'CLOSE':
        return (
          <svg className={cn(iconClass, "text-red-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'CREATED':
      case 'DRAFT':
        return (
          <svg className={cn(iconClass, "text-green-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
        );
      case 'STATUS_CHANGED':
        return (
          <svg className={cn(iconClass, "text-blue-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
          </svg>
        );
      case 'ASSIGNED':
        return (
          <svg className={cn(iconClass, "text-purple-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        );
      case 'VERIFIED':
        return (
          <svg className={cn(iconClass, "text-green-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'ACTION_ADDED':
        return (
          <svg className={cn(iconClass, "text-indigo-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'ACTION_COMPLETED':
        return (
          <svg className={cn(iconClass, "text-green-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
          </svg>
        );
      case 'STARTED':
      case 'IN PROGRESS':
        return (
          <svg className={cn(iconClass, "text-blue-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h1m4 0h1m-6 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'SUBMIT':
      case 'UNDER REVIEW':
        return (
          <svg className={cn(iconClass, "text-orange-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'APPROVE':
        return (
          <svg className={cn(iconClass, "text-green-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
      case 'COMPLETE':
      case 'RESOLVED':
        return (
          <svg className={cn(iconClass, "text-green-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        );
      case 'CANCEL':
      case 'CANCELLED':
        return (
          <svg className={cn(iconClass, "text-red-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        );
      case 'HOLD':
      case 'ON HOLD':
        return (
          <svg className={cn(iconClass, "text-yellow-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        );
      case 'UPDATED':
        return (
          <svg className={cn(iconClass, "text-yellow-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
          </svg>
        );
      default:
        return (
          <svg className={cn(iconClass, "text-gray-600")} fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        );
    }
  };

  // Function to get action badge color
  const getActionBadgeColor = (action) => {
    switch (action?.toUpperCase()) {
      case 'OPENED':
      case 'OPEN':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'CLOSED':
      case 'CLOSE':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'CREATED':
      case 'DRAFT':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'STATUS_CHANGED':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'ASSIGNED':
        return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'VERIFIED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'ACTION_ADDED':
        return 'bg-indigo-100 text-indigo-800 border-indigo-200';
      case 'ACTION_COMPLETED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'STARTED':
      case 'IN PROGRESS':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'SUBMIT':
      case 'UNDER REVIEW':
        return 'bg-orange-100 text-orange-800 border-orange-200';
      case 'APPROVE':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'COMPLETE':
      case 'RESOLVED':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'CANCEL':
      case 'CANCELLED':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'HOLD':
      case 'ON HOLD':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'UPDATED':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div 
      className="p-4 flex-1 overflow-y-auto advanced-table-scroll"
      style={{ 
        scrollbarWidth: 'thin',
        minHeight: 0,
        maxHeight: '100%'
      }}
    >
      {/* Loading State */}
      {isLoading && (
        <div className={cn("text-center py-8", theme.text.muted)}>
          <div className="mb-4">
            <svg className="animate-spin w-8 h-8 mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
            </svg>
          </div>
          <div className="text-sm">Loading timeline...</div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className={cn("text-center py-8", theme.text.muted)}>
          <div className="mb-4">
            <svg className="w-8 h-8 mx-auto text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <div className="text-sm text-red-600 mb-2">Error loading timeline</div>
          <div className="text-xs text-gray-500">{error.message || error}</div>
        </div>
      )}

      {/* Timeline Content */}
      {!isLoading && !error && (
        <div className="space-y-4">
          {timelineEntries.length > 0 ? (
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200 dark:bg-gray-700"></div>
              
              {timelineEntries.map((entry, index) => (
                <div key={entry.id || index} className="relative flex items-start gap-4 pb-6">
                  {/* Timeline dot */}
                  <div className={cn(
                    "relative z-10 flex items-center justify-center w-8 h-8 rounded-full border-2",
                    theme.colors.surface,
                    "border-gray-300 dark:border-gray-600"
                  )}>
                    {getActionIcon(entry.action)}
                  </div>
                  
                  {/* Timeline content */}
                  <div className={cn(
                    "flex-1 min-w-0 p-4 rounded-lg border",
                    theme.border.subtle,
                    theme.colors.background,
                    "hover:border-opacity-60 transition-colors duration-200"
                  )}>
                    {/* Header with action and timestamp */}
                    <div className="flex items-center justify-between gap-3 mb-3">
                      <div className="flex items-center gap-2">
                        <span className={cn(
                          "inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border",
                          getActionBadgeColor(entry.action)
                        )}>
                          {entry.action || 'Unknown Action'}
                        </span>
                      </div>
                      <div className={cn("text-xs", theme.text.muted)}>
                        {formatDateDDMMYYYY(entry.performedAt)}
                      </div>
                    </div>
                    
                    {/* Performed by */}
                    {entry.user && (
                      <div className={cn("flex items-center gap-2 mb-2", theme.text.secondary)}>
                        <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        <span className="text-xs">
                          Performed by: <span className="font-medium">{entry.user.userDisplayName || entry.user.name || 'Unknown User'}</span>
                        </span>
                      </div>
                    )}
                    
                    {/* Comment */}
                    {entry.comment && (
                      <div className={cn("mt-3 p-3 rounded-md", theme.colors.backgroundAlt)}>
                        <div className={cn("text-xs font-medium mb-1", theme.text.secondary)}>Comment:</div>
                        <div className={cn("text-sm", theme.text.primary)}>
                          {entry.comment}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className={cn("text-center py-8", theme.text.muted)}>
              <div className="mb-4">
                <svg className="w-12 h-12 mx-auto text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="text-sm font-medium mb-2">No timeline entries found</div>
              <div className="text-xs">This Complaint doesn't have any timeline activity yet</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ComplaintTimelineTab;
